// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, CreditCard, ExternalLink, X, Calendar, ChevronDown, ChevronUp, Receipt } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Invoice {
  id: string;
  amount_paid: number;
  created: number;
  currency: string;
  status: string;
  invoice_pdf: string;
  hosted_invoice_url: string;
  description?: string;
}

interface Subscription {
  id: string;
  status: string;
  current_period_end: number;
  current_period_start: number;
  plan?: {
    nickname?: string;
    amount: number;
    currency: string;
    interval: string;
  };
}

interface BillingData {
  subscriptions: Subscription[];
  invoices: Invoice[];
}

interface ProfileMembership {
  id: string;
  membership_slug: string;
  is_active: boolean;
  price_paid: number;
  billing_cycle_end: string | null;
  profile_id?: string;
  memberships: {
    name: string;
    billing_type: string;
    is_addon: boolean;
    price_cents: number;
  };
  profiles?: {
    user_id: string;
    subscriptions?: Array<{
      stripe_subscription_id: string;
      plan_id: string;
      status: string;
    }>;
  };
}

export function BillingHistory() {
  const { user } = useAuth();
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [profileMemberships, setProfileMemberships] = useState<ProfileMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [cancellingMembership, setCancellingMembership] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState<ProfileMembership | null>(null);
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});
  const [subscriptionInvoices, setSubscriptionInvoices] = useState<Record<string, Invoice[]>>({});

  useEffect(() => {
    if (user) {
      fetchBillingData();
      fetchProfileMemberships();
    }
  }, [user]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const { data, error } = await invokeEdgeFunction('manage-subscription', {
        body: { action: 'get_details' }
      });

      if (error) throw error;

      setBillingData(data);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Failed to load billing history');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileMemberships = async () => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, active_membership_slug, user_id')
        .eq('user_id', user?.id)
        .single();

      console.log('🔍 Profile data:', profileData, 'Error:', profileError);
      if (!profileData) {
        console.log('❌ No profile found for user');
        return;
      }

      // Fetch paid subscriptions from profile_memberships (WITHOUT embedding subscriptions)
      const { data, error } = await supabase
        .from('profile_memberships')
        .select(`
          id,
          membership_slug,
          is_active,
          price_paid,
          billing_cycle_end,
          profile_id,
          memberships (
            name,
            billing_type,
            is_addon,
            price_cents
          ),
          profiles!profile_memberships_profile_id_fkey!inner (
            user_id
          )
        `)
        .eq('profile_id', profileData.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      console.log('📊 Profile memberships raw data:', data);
      console.log('📊 Profile memberships error:', error);

      if (error) throw error;

      // Fetch subscriptions separately
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('subscriptions')
        .select('stripe_subscription_id, plan_id, status')
        .eq('user_id', profileData.user_id);

      console.log('📊 Subscriptions data:', subscriptionsData);
      console.log('📊 Subscriptions error:', subscriptionsError);

      if (subscriptionsError) {
        console.error('⚠️ Error fetching subscriptions:', subscriptionsError);
      }

      // Transform the data - memberships comes as array from join
      const transformedData = (data || []).map(item => {
        // Handle the nested profiles array from the join
        const profiles = Array.isArray(item.profiles) ? item.profiles[0] : item.profiles;
        
        return {
          ...item,
          memberships: Array.isArray(item.memberships) ? item.memberships[0] : item.memberships,
          profiles: profiles ? {
            user_id: profiles.user_id,
            subscriptions: subscriptionsData || []
          } : undefined
        };
      }) as ProfileMembership[];

      // Also fetch the active membership from the profile to show athlete/free memberships
      if (profileData.active_membership_slug) {
        const { data: activeMembership, error: membershipError } = await supabase
          .from('memberships')
          .select('name, billing_type, is_addon, price_cents')
          .eq('slug', profileData.active_membership_slug)
          .single();

        if (!membershipError && activeMembership) {
          // Check if this membership is not already in the list
          const alreadyExists = transformedData.some(m => m.membership_slug === profileData.active_membership_slug);
          
          if (!alreadyExists) {
            // Add the active membership from profile
            transformedData.unshift({
              id: `profile-${profileData.id}`,
              membership_slug: profileData.active_membership_slug,
              is_active: true,
              price_paid: activeMembership.price_cents,
              billing_cycle_end: null,
              profile_id: profileData.id,
              memberships: {
                name: activeMembership.name,
                billing_type: activeMembership.billing_type,
                is_addon: activeMembership.is_addon,
                price_cents: activeMembership.price_cents
              }
            });
          }
        }
      }

      console.log('✅ Found memberships:', transformedData);
      setProfileMemberships(transformedData);
    } catch (error) {
      console.error('❌ Error fetching profile memberships:', error);
      toast.error('Failed to load subscription details');
    }
  };

  const handleManageSubscription = async () => {
    try {
      setManagingSubscription(true);
      const { data, error } = await invokeEdgeFunction('customer-portal');

      if (error) throw error;

      // Open Stripe customer portal in new tab
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Failed to open billing portal');
    } finally {
      setManagingSubscription(false);
    }
  };

  const handleCancelMembership = async () => {
    if (!selectedMembership) return;

    try {
      setCancellingMembership(selectedMembership.id);

      // Find the stripe subscription ID for this membership
      const stripeSubscriptionId = (selectedMembership as any).profiles?.subscriptions?.find(
        (sub: any) => sub.plan_id === selectedMembership.membership_slug && sub.status === 'active'
      )?.stripe_subscription_id;

      console.log('🔍 Cancelling subscription:', { 
        membershipId: selectedMembership.id, 
        membershipSlug: selectedMembership.membership_slug,
        stripeSubscriptionId 
      });

      // If there's a Stripe subscription, cancel it first
      if (stripeSubscriptionId) {
        console.log('📞 Calling manage-subscription to cancel Stripe subscription');
        const { data: cancelData, error: cancelError } = await invokeEdgeFunction('manage-subscription', {
          body: { 
            action: 'cancel', 
            subscriptionId: stripeSubscriptionId 
          }
        });

        if (cancelError) {
          console.error('❌ Error cancelling in Stripe:', cancelError);
          throw cancelError;
        }

        console.log('✅ Stripe cancellation successful:', cancelData);
      } else {
        console.log('⚠️ No active Stripe subscription found, proceeding with database-only cancellation');
      }

      // Update profile_memberships to set is_active = false
      const { error: updateError } = await supabase
        .from('profile_memberships')
        .update({ is_active: false })
        .eq('id', selectedMembership.id);

      if (updateError) {
        console.error('❌ Error updating profile_memberships:', updateError);
        throw updateError;
      }

      toast.success('Subscription cancelled successfully. You will retain access until the end of your billing period.');
      setShowCancelDialog(false);
      setSelectedMembership(null);
      
      // Refresh data
      fetchProfileMemberships();
    } catch (error) {
      console.error('❌ Error cancelling membership:', error);
      toast.error('Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setCancellingMembership(null);
    }
  };

  const toggleCardExpansion = async (membershipId: string, membershipSlug: string, profileId?: string) => {
    const isCurrentlyExpanded = expandedCards[membershipId];
    
    setExpandedCards(prev => ({
      ...prev,
      [membershipId]: !isCurrentlyExpanded
    }));

    // Fetch invoices for this specific membership if expanding and not already loaded
    if (!isCurrentlyExpanded && !subscriptionInvoices[membershipId]) {
      try {
        console.log('🔍 Fetching billing history for:', { membershipId, membershipSlug, profileId });
        
        // Query the billing_history table using the subscription_id (which is the profile_membership id)
        const { data: billingHistory, error } = await supabase
          .from('billing_history')
          .select('*')
          .eq('subscription_id', membershipId)
          .order('payment_date', { ascending: false });

        console.log('📊 Billing history result:', { billingHistory, error });

        if (error) throw error;

        // Convert billing history to invoice format
        const invoices = (billingHistory || []).map(item => ({
          id: item.id,
          amount_paid: item.total_amount || 0,
          created: Math.floor(new Date(item.payment_date).getTime() / 1000),
          currency: 'usd',
          status: item.status || 'paid',
          invoice_pdf: '',
          hosted_invoice_url: '',
          description: item.notes || 'Membership payment'
        }));
        
        console.log('✅ Converted invoices:', invoices);
        
        setSubscriptionInvoices(prev => ({
          ...prev,
          [membershipId]: invoices
        }));
      } catch (error) {
        console.error('❌ Error loading subscription invoices:', error);
      }
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatAmount = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
      case 'active':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'failed':
      case 'canceled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Billing & Subscriptions</h1>
        <p className="text-muted-foreground">Manage your subscription and view billing history</p>
      </div>

      {/* Active Subscriptions from Profile Memberships */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Active Subscriptions
          </CardTitle>
          <CardDescription>Your current subscription plans</CardDescription>
        </CardHeader>
        <CardContent>
          {profileMemberships.length > 0 ? (
            <div className="space-y-4">
              {profileMemberships.map((membership) => (
                <Collapsible
                  key={membership.id}
                  open={expandedCards[membership.id]}
                  onOpenChange={() => toggleCardExpansion(membership.id, membership.membership_slug, membership.profile_id)}
                >
                  <div className="p-6 border-2 rounded-lg space-y-4 bg-card">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-2xl">
                            {membership.memberships?.name || 'Unknown Subscription'}
                          </h3>
                          <Badge variant="default" className="text-xs">Active</Badge>
                          {membership.memberships?.is_addon && (
                            <Badge variant="secondary" className="text-xs">Add-on</Badge>
                          )}
                        </div>
                        <div className="flex items-baseline gap-4 flex-wrap">
                          <p className="text-xl font-bold text-primary">
                            ${((membership.memberships?.price_cents || membership.price_paid) / 100).toFixed(2)}
                            <span className="text-sm font-normal text-muted-foreground ml-1">
                              /{membership.memberships?.billing_type === 'monthly' ? 'mo' : 'once'}
                            </span>
                          </p>
                          {membership.billing_cycle_end && membership.memberships?.billing_type === 'monthly' && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              <span>Next billing: <strong>{new Date(membership.billing_cycle_end).toLocaleDateString()}</strong></span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                          >
                            {expandedCards[membership.id] ? (
                              <>
                                <ChevronUp className="h-4 w-4" />
                                Hide History
                              </>
                            ) : (
                              <>
                                <ChevronDown className="h-4 w-4" />
                                View History
                              </>
                            )}
                          </Button>
                        </CollapsibleTrigger>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMembership(membership);
                            setShowCancelDialog(true);
                          }}
                          className="gap-2"
                        >
                          <X className="h-4 w-4" />
                          Cancel
                        </Button>
                      </div>
                    </div>

                    <CollapsibleContent className="space-y-2">
                      <div className="pt-4 border-t">
                        <h4 className="text-sm font-semibold mb-3">Payment History</h4>
                        {subscriptionInvoices[membership.id]?.length > 0 ? (
                          <div className="rounded-md border">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b bg-muted/50">
                                  <th className="text-left p-3 text-sm font-medium">Amount</th>
                                  <th className="text-left p-3 text-sm font-medium">Date</th>
                                  <th className="text-left p-3 text-sm font-medium">Status</th>
                                  <th className="text-right p-3 text-sm font-medium">Receipt</th>
                                </tr>
                              </thead>
                              <tbody>
                                {subscriptionInvoices[membership.id].map((invoice) => (
                                  <tr key={invoice.id} className="border-b last:border-0 hover:bg-muted/30">
                                    <td className="p-3">
                                      <span className="font-medium">
                                        {formatAmount(invoice.amount_paid, invoice.currency)}
                                      </span>
                                    </td>
                                    <td className="p-3 text-sm text-muted-foreground">
                                      {formatDate(invoice.created)}
                                    </td>
                                    <td className="p-3">
                                      <Badge variant={getStatusColor(invoice.status)} className="text-xs">
                                        {invoice.status}
                                      </Badge>
                                    </td>
                                    <td className="p-3 text-right">
                                      {invoice.hosted_invoice_url && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                                          className="gap-1"
                                        >
                                          <Receipt className="h-3 w-3" />
                                          Receipt
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground py-4 text-center">
                            No payment history available for this subscription
                          </p>
                        )}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p className="mb-2">No active subscriptions found</p>
              <p className="text-xs">Debug: Check console for details</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card>
        <CardHeader>
          <CardTitle>Billing History</CardTitle>
          <CardDescription>Your past invoices and payments</CardDescription>
        </CardHeader>
        <CardContent>
          {billingData?.invoices && billingData.invoices.length > 0 ? (
            <div className="space-y-3">
              {billingData.invoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {formatAmount(invoice.amount_paid, invoice.currency)}
                      </span>
                      <Badge variant={getStatusColor(invoice.status)}>
                        {invoice.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {invoice.description || 'Subscription payment'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(invoice.created)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {invoice.hosted_invoice_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                        className="gap-1"
                      >
                        <Receipt className="h-3 w-3" />
                        Receipt
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No billing history found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Subscription Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel {selectedMembership?.memberships?.name || 'Subscription'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this subscription? You'll retain access until the end of your current billing period, but won't be charged again.
              {!selectedMembership?.memberships?.is_addon && (
                <span className="block mt-2 font-semibold text-destructive">
                  Warning: Cancelling your base membership will affect access to all features.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelMembership}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={cancellingMembership === selectedMembership?.id}
            >
              {cancellingMembership === selectedMembership?.id ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Yes, Cancel Subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}